import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { getFirestore, doc, getDoc } from '@firebase/firestore';
import { getAuth } from '@firebase/auth';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useFacility } from '../../context/FacilityContext';
import { changePassword } from '../../firebase/auth';
import './AccountSettings.css';

function AccountSettings() {
  const { facilityId } = useFacility();
  const { userName, userEmail } = useAuth('');

  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [infoAlert, setInfoAlert] = useState('');
  const [errorAlert, setErrorAlert] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const resetAlerts = () => {
    setInfoAlert('');
    setErrorAlert('');
  };

  const [pinCode, setPinCode] = useState(null);
  const [pinLoading, setPinLoading] = useState(true);

  useEffect(() => {
    const fetchPin = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          setPinLoading(false);
          return;
        }

        const db = getFirestore();
        const pinDocRef = doc(db, 'users', user.uid);
        const pinDocSnap = await getDoc(pinDocRef);

        if (pinDocSnap.exists()) {
          const data = pinDocSnap.data();

          setPinCode(data?.pinCode ?? null);
        } else {
          setPinCode(null);
        }
      } catch (err) {
        setErrorAlert('Could not load your PIN. Please try again later.');
      } finally {
        setPinLoading(false);
      }
    };

    fetchPin();
  }, []);

  const changePass = async (event) => {
    resetAlerts();
    event.preventDefault();
    if (newPassword !== passwordConfirm) {
      setErrorAlert('Passwords do not match. Please try again.');
      return;
    }
    if (newPassword.length < 8) {
      setErrorAlert('Password has to be longer than 8 characters!');
      return;
    }
    setIsLoading(true);
    try {
      const result = await changePassword(password, newPassword);
      if (!result) {
        setInfoAlert('Password updated successfully!');
        setPassword('');
        setPasswordConfirm('');
        setNewPassword('');
      } else {
        setErrorAlert(result);
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setErrorAlert(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
      <Typography variant="h4" component="h1" gutterBottom>
        Welcome, {userName}
      </Typography>
      <Typography variant="body1">{userEmail}</Typography>

      <Box className="SettingsContainer" sx={{ mt: 4 }}>
        <Typography variant="h6">Your Staff PIN (for QuickCare)</Typography>
        {pinLoading ? (
          <CircularProgress size={24} sx={{ mt: 1 }} />
        ) : pinCode ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your PIN: {pinCode}
            </Typography>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mt: 1 }}>
            No PIN assigned. Ask an administrator to generate one for you.
          </Alert>
        )}
      </Box>

      <Box className="SettingsContainer" sx={{ mt: 6 }}>
        <Typography variant="h6">Change Password</Typography>

        {infoAlert && (
          <Alert
            severity="success"
            onClose={() => setInfoAlert('')}
            sx={{ mb: 2 }}
          >
            {infoAlert}
          </Alert>
        )}
        {errorAlert && (
          <Alert
            severity="error"
            onClose={() => setErrorAlert('')}
            sx={{ mb: 2 }}
          >
            {errorAlert}
          </Alert>
        )}

        <form onSubmit={changePass}>
          <input
            hidden
            type="text"
            name="username"
            value={userEmail}
            readOnly
          />

          <TextField
            label="Current Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            autoComplete="current-password"
            required
            fullWidth
          />
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            autoComplete="new-password"
            required
            fullWidth
            inputProps={{ minLength: 8 }}
            error={newPassword.length > 0 && newPassword.length < 8}
            helperText={
              newPassword.length > 0 &&
              newPassword.length < 8 &&
              'Password has to be longer than 8 characters!'
            }
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            margin="normal"
            autoComplete="new-password"
            required
            fullWidth
            inputProps={{ minLength: 8 }}
            error={newPassword !== passwordConfirm}
            helperText={
              newPassword !== passwordConfirm && 'Passwords do not match!'
            }
          />

          <Button
            className="FormButton"
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ mt: 2 }}
          >
            Change
          </Button>
        </form>
      </Box>
    </DashboardLayout>
  );
}

AccountSettings.propTypes = {};

export default AccountSettings;
